import { useDispatch, useSelector } from "react-redux";
import { Link, useParams, useSearchParams } from "react-router-dom";
import NavigationTop from "../../components/NavigationTop/NavigationTop";
import { useEffect } from "react";
import { fetchDetailNews, clearDetailNews } from "../../store/slices/detailNewsSlice";
import styles from "./News.module.css";
import { Helmet } from "react-helmet-async";
import VideoPlayer from "../../components/VideoPlayer/VideoPlayer";
import TutorialCustomHtmlBlock from "../../components/TutorialCustomHtmlBlock/TutorialCustomHtmlBlock";
import "./DetailNews.css";
import { resolveMediaUrl } from "../../utils/mediaUrl";

const DetailNews = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get('preview');
  const dispatch = useDispatch();
  const { detailNews, status, error } = useSelector((state) => state.detailNews);

  const metaTitle = detailNews
    ? `${detailNews.title} – Distortion Grid News`
    : "News – Distortion Grid Guide";
  const metaDescription =
    detailNews?.short_description ||
    "Latest news from the Distortion Grids Database.";

  useEffect(() => {
    if (slug) {
      dispatch(clearDetailNews());
      const promise = dispatch(fetchDetailNews({ slug, previewToken }));
      return () => promise.abort();
    }
  }, [dispatch, slug, previewToken]);

  if ((status === "loading" || status === "idle") && !detailNews) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', fontSize: '18px', color: '#666' }}>
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDescription} />
        </Helmet>
        Loading news...
      </div>
    );
  }

  if (status === "failed" && !detailNews) {
    const isNotFound = error?.notFound;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', fontSize: '18px', color: isNotFound ? '#666' : '#e74c3c', gap: '20px' }}>
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDescription} />
        </Helmet>
        <div>{isNotFound ? 'News not found.' : 'Failed to load news. Please try again.'}</div>
        {!isNotFound && (
          <button
            onClick={() => dispatch(fetchDetailNews({ slug, previewToken }))}
            style={{ padding: '10px 20px', backgroundColor: '#2f57bf', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '16px' }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (!detailNews) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', fontSize: '18px', color: '#666' }}>
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDescription} />
        </Helmet>
        News not found.
      </div>
    );
  }

  return (
    <section>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
      </Helmet>
      <div className={styles.titleBlock}>
        {detailNews && (
          <NavigationTop
            title={detailNews.title}
            text={detailNews.short_description}
          />
        )}
      </div>
      <div className={styles.blockDetail}>
        {(detailNews.status === 'draft' || new Date(detailNews.published_at) > new Date()) && (
          <div className="draft-banner">
            {detailNews.status === 'draft'
              ? 'Предпросмотр (черновик) — эта страница не опубликована'
              : 'Предпросмотр — эта страница ещё не опубликована публично'}
          </div>
        )}
        {status === "loading" && !detailNews?.blocks ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px', fontSize: '16px', color: '#666' }}>
            Loading news content...
          </div>
        ) : (
          detailNews?.blocks?.map((block) => (
            <div key={block.id}>
              {block.type === "image" ? (
                <img
                  className="news-image-block"
                  src={resolveMediaUrl(block.image)}
                  alt={block.alt_text}
                />
              ) : block.type === "video" ? (
                <div className="news-video-block">
                  {block.video_url ? (
                    <VideoPlayer
                      src={block.video_url}
                      title={block.title || "News video"}
                      videoTitle={block.title}
                      thumbnail={resolveMediaUrl(block.thumbnail)}
                    />
                  ) : null}
                </div>
              ) : block.type === "custom_html" ? (
                <TutorialCustomHtmlBlock block={block} />
              ) : (
                // block.text is sanitized server-side via nh3 (same as DetailTutorialV2)
                <div
                  className="news-content"
                  dangerouslySetInnerHTML={{ __html: block.text || '' }} // nosec: server-sanitized via nh3
                />
              )}
            </div>
          ))
        )}
        <div className="news-back-link">
          <Link to="/news">← Back to News</Link>
        </div>
      </div>
    </section>
  );
};

export default DetailNews;
